/**
 * /mail (default tab → connections) — OMS connections (OCP-119).
 *
 * Lists every OAuth token whose scopes include `mail:*`. Per-card:
 *   - Customer-editable label (falls back to connection_id)
 *   - Connection_id, scopes, status, expiry
 *   - Disconnect button (revokes the underlying token + 2-sided
 *     state via OAuth-server's standard revoke flow)
 *   - View stats link → /mail/stats?connection_id=… (deferred — that
 *     page lands with the next OMS-stats ticket)
 *
 * Cross-link with OCP-118: same underlying portal_oauth_tokens rows
 * show up on /api-keys for the master cross-product view.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

import { MailReadOnlyNotice } from '../components/MailReadOnlyNotice';
import { ApiError, api } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';
import { contractGateMessage } from '../components/BusinessCapacityGate';
import { useOrderReview } from '../components/OrderReviewStep';
import { fmtMoney, fmtNumber } from '../lib/format-money';
import { fmtDate } from '../lib/format-time';

interface Connection {
  id: string;
  client_id: string;
  product_family: string;
  scopes: string[];
  connection_id: string | null;
  label: string | null;
  hash_prefix: string;
  issued_at: string;
  expires_at: string;
  refresh_expires_at: string | null;
  revoked_at: string | null;
  /** OCP-779 - `revoking`: disconnected here, OMS has not confirmed the Mail key is dead yet. */
  status: 'active' | 'revoking' | 'revoked' | 'expired';
}

const STATUS_BADGE: Record<Connection['status'], { cls: string }> = {
  active: { cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
  revoking: { cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200' },
  revoked: { cls: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  expired: { cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200' },
};

interface MailProduct {
  id: string;
  display_name: string;
  product_family: string;
  price_eur_cents: number;
  billing_period: string;
  features: Record<string, unknown>;
  description?: string | null;
  marketing_bullets?: string[] | null;
}

interface ActiveSubscription {
  product_id: string;
  status: string;
}

export function MailConnectionsPage() {
  const { t } = useTranslation('mail-connections');
  const [items, setItems] = useState<Connection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  // OCP-545 - renaming or disconnecting an integration changes the mail
  // setup: owner/admin only, enforced by the API.
  const { canWrite } = useMailWriteAccess();
  // OCP-158 — in-place subscribe state.
  const [mailProducts, setMailProducts] = useState<MailProduct[] | null>(null);
  const [hasActiveMailSub, setHasActiveMailSub] = useState<boolean | null>(null);
  const [checkoutPending, setCheckoutPending] = useState<string | null>(null);
  // OCP-161 — credit balance for the customer (top-up sends remaining).
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [topupOpen, setTopupOpen] = useState(false);

  // OCP-158 — check subscription state + load Mail-family products in
  // parallel with the connections list. Failures degrade silently:
  // if either request fails, we just don't render the subscribe block
  // (better than blocking the connections list, which works without).
  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      const [subs, products, credits] = await Promise.all([
        api<{ items: ActiveSubscription[] }>('/v1/portal/me/subscriptions', {
          signal: ac.signal,
        }).catch(() => null),
        api<{ items: MailProduct[] }>('/v1/portal/products', {
          signal: ac.signal,
        }).catch(() => null),
        api<{ balance: number }>('/v1/portal/me/mail-credits', {
          signal: ac.signal,
        }).catch(() => null),
      ]);
      if (ac.signal.aborted) return;
      if (credits) setCreditBalance(credits.balance);
      if (subs) {
        const ACTIVE = new Set(['active', 'trialing', 'past_due']);
        setHasActiveMailSub(
          subs.items.some(
            (s) =>
              ACTIVE.has(s.status) &&
              (s.product_id.startsWith('mail-') || s.product_id.startsWith('oms-')),
          ),
        );
      } else {
        setHasActiveMailSub(false); // assume no — show subscribe block as fallback
      }
      if (products) {
        setMailProducts(products.items.filter((p) => p.product_family === 'oms'));
      }
    })();
    return () => ac.abort();
  }, []);

  // OCP-526 - the order review step (services, price, billing cycle, term +
  // the ordering button directly under it, AGB V4 1.2). It also hosts the
  // OCP-524 responsible-contact capture as its first step when the account
  // still owes one - one dialog, not two.
  const orderReview = useOrderReview();

  const runBuyMailPlan = useCallback(
    async (productId: string) => {
      const product = mailProducts?.find((p) => p.id === productId);
      setCheckoutPending(productId);
      setError(null);
      try {
        // Free products bypass Paddle (OCP-169). The checkout endpoint
        // rejects them with 404 `product_not_purchasable` since they
        // have no Paddle SKU.
        if (product && product.price_eur_cents === 0) {
          const r = await api<{
            subscription_id: string;
            redirect_url: string;
          }>('/v1/portal/subscriptions/activate-free', {
            method: 'POST',
            body: { product_id: productId },
          });
          // Already on /mail — full reload to pick up the new sub
          // state without a stale React tree.
          window.location.assign(r.redirect_url);
          return;
        }
        const r = await api<{ checkout_url: string }>('/v1/portal/checkout/create', {
          method: 'POST',
          body: { product_id: productId },
        });
        window.location.assign(r.checkout_url);
      } catch (err) {
        setCheckoutPending(null);
        // OCP-524 / OCP-534 - the Terms gates carry customer-facing copy.
        const gate = contractGateMessage(err);
        if (gate) {
          setError(gate);
          return;
        }
        if (err instanceof ApiError) {
          if (err.status === 409) {
            const body = err.body as { error?: string } | null;
            if (body?.error === 'free_subscription_already_active') {
              setError(t('error.alreadyActive'));
              return;
            }
          }
          setError(
            err.status === 503 ? t('error.checkoutUnavailable') : t('error.checkoutHttp', { status: err.status }),
          );
        } else {
          setError(t('error.checkout'));
        }
      }
    },
    [mailProducts, t],
  );

  const onBuyMailPlan = useCallback(
    (productId: string) => {
      orderReview.start({
        productId,
        onOrder: () => runBuyMailPlan(productId),
      });
    },
    [orderReview, runBuyMailPlan],
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    setError(null);
    try {
      const r = await api<{ items: Connection[] }>('/v1/portal/mail/connections', {
        signal,
      });
      setItems(r.items);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError) setError(t('error.loadHttp', { status: err.status }));
      else setError(t('error.load'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const onSaveLabel = useCallback(
    async (id: string) => {
      const next = labelDraft.trim();
      try {
        await api<Connection>(`/v1/portal/mail/connections/${id}/label`, {
          method: 'PATCH',
          body: { label: next.length === 0 ? null : next },
        });
        setEditingLabelId(null);
        setLabelDraft('');
        void load();
      } catch (err) {
        if (err instanceof ApiError) setError(t('error.labelHttp', { status: err.status }));
        else setError(t('error.label'));
      }
    },
    [labelDraft, load, t],
  );

  const onDisconnect = useCallback(
    async (id: string) => {
      if (!confirm(t('disconnectConfirm'))) return;
      try {
        await api<Connection>(`/v1/portal/mail/connections/${id}/disconnect`, {
          method: 'POST',
        });
        void load();
      } catch (err) {
        if (err instanceof ApiError) setError(t('error.disconnectHttp', { status: err.status }));
        else setError(t('error.disconnect'));
      }
    },
    [load, t],
  );

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('intro')}</p>
      </div>

      <MailReadOnlyNotice />

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {/* OCP-158 — in-place subscribe block. Renders when the customer
          has no active Mail subscription. Sits above the connections
          list so it's the first thing they see. */}
      {hasActiveMailSub === false && mailProducts && mailProducts.length > 0 && (
        <SubscribeBlock
          products={mailProducts.filter(
            (p) => (p.features as { kind?: string })?.kind !== 'topup',
          )}
          pending={checkoutPending}
          onBuy={onBuyMailPlan}
        />
      )}

      {/* OCP-161 — credits panel + Buy-more-sends. Renders when the
          customer has an active Mail subscription, surfacing the
          pre-purchased credit balance + a CTA to top up before they
          actually hit the subscription quota. */}
      {hasActiveMailSub === true && (
        <CreditsPanel
          balance={creditBalance}
          onOpenTopup={() => setTopupOpen(true)}
        />
      )}
      {topupOpen && mailProducts && (
        <TopupModal
          products={mailProducts.filter(
            (p) => (p.features as { kind?: string })?.kind === 'topup',
          )}
          pending={checkoutPending}
          onClose={() => setTopupOpen(false)}
          onBuy={onBuyMailPlan}
        />
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('loading')}</p>}

      {items && items.length === 0 && hasActiveMailSub !== false && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('emptyBefore')} <em>{t('emptyPath')}</em>{t('emptyAfter')}
        </div>
      )}

      <div className="space-y-3">
        {items?.map((c) => (
          <article
            key={c.id}
            data-testid={`connection-card-${c.id}`}
            className={`rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm ${
              c.status !== 'active' ? 'opacity-70' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                {editingLabelId === c.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      placeholder={c.connection_id ?? ''}
                      className="flex-1 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => void onSaveLabel(c.id)}
                      className="rounded bg-brand-500 px-2 py-1 text-xs font-medium text-white hover:bg-brand-600"
                    >
                      {t('card.save')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingLabelId(null);
                        setLabelDraft('');
                      }}
                      className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                    >
                      {t('card.cancel')}
                    </button>
                  </div>
                ) : (
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {c.label ?? c.connection_id ?? c.client_id}
                    {canWrite && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingLabelId(c.id);
                        setLabelDraft(c.label ?? '');
                      }}
                      data-testid={`edit-label-${c.id}`}
                      className="ml-2 text-xs font-normal text-brand-700 underline"
                    >
                      {c.label ? t('card.rename') : t('card.setLabel')}
                    </button>
                    )}
                  </h2>
                )}
                <p className="mt-0.5 text-xs text-gray-500">
                  {t('card.connectionId')}{' '}
                  <code className="font-mono">{c.connection_id ?? c.hash_prefix + '…'}</code>
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {t('card.scopes', { scopes: c.scopes.join(', ') })}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {t('card.issued', { date: formatDate(c.issued_at) })}
                  {c.expires_at ? t('card.expires', { date: formatDate(c.expires_at) }) : ''}
                </p>
              </div>
              <span
                data-testid={`connection-status-${c.id}`}
                title={c.status === 'revoking' ? t('status.revokingHint') : undefined}
                className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[c.status].cls}`}
              >
                {t(`status.${c.status}`)}
              </span>
            </div>
            {c.status === 'revoking' && (
              <p
                data-testid={`connection-revoking-${c.id}`}
                className="mt-2 text-xs text-amber-800 dark:text-amber-300"
              >
                {t('status.revokingHint')}
              </p>
            )}

            <div className="mt-3 flex items-center gap-3">
              <Link
                to={`/mail/stats?connection_id=${encodeURIComponent(c.connection_id ?? c.id)}`}
                className="text-xs text-brand-700 underline"
              >
                {t('card.viewStats')}
              </Link>
              {c.status === 'active' && canWrite && (
                <button
                  type="button"
                  onClick={() => void onDisconnect(c.id)}
                  className="ml-auto text-xs text-red-700 dark:text-red-300 underline"
                >
                  {t('card.disconnect')}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      {orderReview.element}
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso);
}

/**
 * OCP-158 — in-place subscribe block. Sits above the connections list
 * when the customer has no active Mail subscription. Tiles of available
 * plans + per-tile Buy CTA that goes through the same Paddle hosted-
 * checkout flow ProductsPage uses.
 */
function SubscribeBlock({
  products,
  pending,
  onBuy,
}: {
  products: MailProduct[];
  pending: string | null;
  onBuy: (productId: string) => void;
}) {
  const { t } = useTranslation('mail-connections');
  // Sort: free first (so we have a clear entry-point), then by price.
  const sorted = [...products].sort((a, b) => a.price_eur_cents - b.price_eur_cents);
  return (
    <section
      data-testid="subscribe-block"
      className="mb-6 rounded-lg border border-brand-200 bg-brand-50/40 p-5 shadow-sm"
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {t('subscribe.title')}
      </h2>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{t('subscribe.lead')}</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((p) => (
          <article
            key={p.id}
            className="flex flex-col justify-between rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm"
          >
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {p.display_name}
              </h3>
              <div className="mt-1 text-xs text-gray-500">
                {p.price_eur_cents === 0
                  ? t('subscribe.free')
                  : t('subscribe.pricePer', { price: fmtMoney(p.price_eur_cents), period: p.billing_period === 'yearly' ? t('subscribe.year') : t('subscribe.month') })}
              </div>
              {p.description && (
                <div className="prose prose-sm mt-2 max-w-none text-xs text-gray-700 dark:text-gray-300 [&_p]:m-0">
                  <ReactMarkdown>{p.description}</ReactMarkdown>
                </div>
              )}
              {p.marketing_bullets && p.marketing_bullets.length > 0 ? (
                <ul className="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {p.marketing_bullets.map((b) => (
                    <li key={b} className="flex items-start gap-1">
                      <span aria-hidden="true" className="text-brand-500">·</span>
                      <span className="prose prose-sm max-w-none [&_p]:m-0">
                        <ReactMarkdown>{b}</ReactMarkdown>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                typeof p.features?.monthly_sends === 'number' && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    {t('subscribe.upTo', { count: fmtNumber(p.features.monthly_sends as number) })}
                  </div>
                )
              )}
            </div>
            <button
              type="button"
              onClick={() => onBuy(p.id)}
              disabled={pending !== null}
              className="mt-4 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {/* OCP-526 - navigational, NOT the ordering button. The button
                  that places the binding offer lives in the review dialog,
                  directly under the order summary. */}
              {pending === p.id ? t('subscribe.opening') : t('subscribe.review')}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

/**
 * OCP-161 — credit balance summary card. Shows the customer's current
 * pre-purchased send credits (top-up balance) + a CTA to buy more
 * before they hit the monthly subscription quota.
 */
function CreditsPanel({
  balance,
  onOpenTopup,
}: {
  balance: number | null;
  onOpenTopup: () => void;
}) {
  const { t } = useTranslation('mail-connections');
  return (
    <section
      data-testid="credits-panel"
      className="mb-6 flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 py-4 shadow-sm"
    >
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {t('credits.title')}
        </div>
        <div className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
          {balance === null ? '—' : fmtNumber(balance)}
        </div>
        <div className="mt-1 text-xs text-gray-500">{t('credits.help')}</div>
      </div>
      <button
        type="button"
        onClick={onOpenTopup}
        className="rounded border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100"
      >
        {t('credits.buyMore')}
      </button>
    </section>
  );
}

/**
 * OCP-161 — modal showing the three top-up tier tiles. Click → Paddle
 * one-off checkout via the same /v1/portal/checkout/create endpoint
 * (paddle products are configured as one-time, the price/billing
 * shape comes from Paddle dashboard).
 */
function TopupModal({
  products,
  pending,
  onClose,
  onBuy,
}: {
  products: MailProduct[];
  pending: string | null;
  onClose: () => void;
  onBuy: (productId: string) => void;
}) {
  const { t } = useTranslation('mail-connections');
  const sorted = [...products].sort((a, b) => a.price_eur_cents - b.price_eur_cents);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold">{t('topup.title')}</h2>
            <p className="mt-1 text-xs text-gray-500" data-testid="mail-topup-lead">{t('topup.leadTokens')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('topup.close')}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
          >
            ✕
          </button>
        </div>

        {sorted.length === 0 ? (
          <p className="text-sm text-gray-500">
            {t('topup.notConfigured')}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {sorted.map((p) => {
              const sends =
                typeof (p.features as { topup_sends?: number })?.topup_sends === 'number'
                  ? (p.features as { topup_sends: number }).topup_sends
                  : 0;
              const discount =
                typeof (p.features as { discount_pct?: number })?.discount_pct === 'number'
                  ? (p.features as { discount_pct: number }).discount_pct
                  : 0;
              return (
                <article
                  key={p.id}
                  className="flex flex-col justify-between rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm"
                >
                  <div>
                    <h3 className="text-sm font-semibold">
                      {sends > 0 ? t('topup.sends', { count: fmtNumber(sends) }) : p.display_name}
                    </h3>
                    <div className="mt-1 text-xs text-gray-500">
                      {t('topup.oneOff', { price: fmtMoney(p.price_eur_cents) })}
                    </div>
                    {p.description && (
                      <div className="prose prose-sm mt-2 max-w-none text-xs text-gray-700 dark:text-gray-300 [&_p]:m-0">
                        <ReactMarkdown>{p.description}</ReactMarkdown>
                      </div>
                    )}
                    {p.marketing_bullets && p.marketing_bullets.length > 0 ? (
                      <ul className="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                        {p.marketing_bullets.map((b) => (
                          <li key={b} className="flex items-start gap-1">
                            <span aria-hidden="true" className="text-brand-500">·</span>
                            <span className="prose prose-sm max-w-none [&_p]:m-0">
                              <ReactMarkdown>{b}</ReactMarkdown>
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      discount > 0 && (
                        <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                          {t('topup.discount', { discount })}
                        </div>
                      )
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onBuy(p.id)}
                    disabled={pending !== null}
                    className="mt-4 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {pending === p.id ? t('topup.opening') : t('topup.buy')}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
